import React from 'react';
import { Typography, Box, Paper, Stack, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

const SpacingExample = ({ size, value, description }) => {
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        border: '1px solid', 
        borderColor: 'gray.g200',
        mb: 2 
      }}
    >
      <Stack direction="row" alignItems="center" spacing={3}>
        <Box
          sx={{
            width: value,
            height: 32,
            backgroundColor: 'primary.main',
            borderRadius: 1,
            minWidth: value,
          }}
        />
        <Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body1" fontWeight={600}>
              {size}
            </Typography>
            <Chip 
              label={`${value}px`} 
              size="small" 
              variant="outlined" 
            />
          </Stack>
          <Typography variant="body2" color="gray.g500">
            {description}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

const LayoutExample = ({ title, spacing, children }) => {
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        border: '1px solid', 
        borderColor: 'gray.g200',
        mb: 3 
      }}
    >
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ backgroundColor: 'gray.g50', p: 2, borderRadius: 1 }}>
        <Stack spacing={spacing}>
          {children}
        </Stack>
      </Box>
      <Typography variant="caption" color="gray.g500" sx={{ mt: 1, display: 'block' }}>
        Spacing: {spacing}
      </Typography>
    </Paper>
  );
};

const Spacing = () => {
  const spacingScale = [
    { size: 'xs', value: 4, description: 'Minimal spacing between small elements' },
    { size: 'sm', value: 8, description: 'Small gaps, tight layouts' },
    { size: 'md', value: 16, description: 'Standard spacing, most common use' },
    { size: 'lg', value: 24, description: 'Larger gaps, section spacing' },
    { size: 'xl', value: 32, description: 'Large spacing, major sections' },
    { size: '2xl', value: 48, description: 'Extra large spacing' },
    { size: '3xl', value: 64, description: 'Maximum spacing for major divisions' },
  ];

  const componentSpacing = [
    { component: 'Button padding', horizontal: '16px', vertical: '8px', usage: 'Standard button padding' },
    { component: 'Card padding', horizontal: '24px', vertical: '24px', usage: 'Content cards and panels' },
    { component: 'Input padding', horizontal: '12px', vertical: '8px', usage: 'Form inputs and fields' },
    { component: 'Navigation item', horizontal: '16px', vertical: '12px', usage: 'Menu and navigation items' },
    { component: 'Table cell', horizontal: '16px', vertical: '12px', usage: 'Data table cells' },
  ];

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Spacing System
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Our spacing system provides consistent rhythm and visual hierarchy throughout 
          the interface using a systematic approach to space.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="8px Base Unit" variant="outlined" />
          <Chip label="Consistent Scale" variant="outlined" />
          <Chip label="Responsive" variant="outlined" />
        </Stack>
      </Stack>

      {/* Spacing Scale */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Spacing Scale
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ mb: 3 }}>
          Our spacing scale is based on an 8px grid system, providing consistent proportions 
          and harmonious layouts.
        </Typography>
        
        {spacingScale.map((space) => (
          <SpacingExample
            key={space.size}
            size={space.size}
            value={space.value}
            description={space.description}
          />
        ))}
      </Box>

      {/* Layout Examples */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Layout Examples
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LayoutExample title="Tight Layout" spacing={1}>
              <Box sx={{ backgroundColor: 'secondary.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 1</Typography>
              </Box>
              <Box sx={{ backgroundColor: 'secondary.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 2</Typography>
              </Box>
              <Box sx={{ backgroundColor: 'secondary.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 3</Typography>
              </Box>
            </LayoutExample>
          </Grid>

          <Grid item xs={12} md={6}>
            <LayoutExample title="Standard Layout" spacing={2}>
              <Box sx={{ backgroundColor: 'success.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 1</Typography>
              </Box>
              <Box sx={{ backgroundColor: 'success.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 2</Typography>
              </Box>
              <Box sx={{ backgroundColor: 'success.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 3</Typography>
              </Box>
            </LayoutExample>
          </Grid>

          <Grid item xs={12} md={6}>
            <LayoutExample title="Comfortable Layout" spacing={3}>
              <Box sx={{ backgroundColor: 'yellow.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 1</Typography>
              </Box>
              <Box sx={{ backgroundColor: 'yellow.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 2</Typography>
              </Box>
              <Box sx={{ backgroundColor: 'yellow.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 3</Typography>
              </Box>
            </LayoutExample>
          </Grid>

          <Grid item xs={12} md={6}>
            <LayoutExample title="Spacious Layout" spacing={4}>
              <Box sx={{ backgroundColor: 'error.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 1</Typography>
              </Box>
              <Box sx={{ backgroundColor: 'error.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 2</Typography>
              </Box>
              <Box sx={{ backgroundColor: 'error.light', p: 2, borderRadius: 1 }}>
                <Typography variant="body2">Item 3</Typography>
              </Box>
            </LayoutExample>
          </Grid>
        </Grid>
      </Box>

      {/* Component Spacing */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Component Spacing
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ mb: 3 }}>
          Standard spacing values used within common UI components.
        </Typography>
        
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'gray.g50' }}>
                <TableCell><strong>Component</strong></TableCell>
                <TableCell><strong>Horizontal</strong></TableCell>
                <TableCell><strong>Vertical</strong></TableCell>
                <TableCell><strong>Usage</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {componentSpacing.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {item.component}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.horizontal}</TableCell>
                  <TableCell>{item.vertical}</TableCell>
                  <TableCell>{item.usage}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Material-UI Spacing */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Material-UI Spacing Usage
        </Typography>
        
        <Paper sx={{ p: 3, backgroundColor: 'gray.g25' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Use Material-UI's spacing system in your components:
          </Typography>
          <Box component="pre" sx={{ 
            backgroundColor: 'gray.g700', 
            color: 'white', 
            p: 2, 
            borderRadius: 1,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem'
          }}>
{`// Using theme spacing
sx={{ 
  padding: theme.spacing(2),     // 16px
  margin: theme.spacing(1, 2),   // 8px 16px
  gap: theme.spacing(3)          // 24px
}}

// Using spacing prop
<Stack spacing={2}>             // 16px gap
<Box sx={{ p: 3, m: 2 }}>      // 24px padding, 16px margin`}
          </Box>
        </Paper>
      </Box>

      {/* Spacing Guidelines */}
      <Paper sx={{ p: 4, backgroundColor: 'secondary.light' }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Spacing Guidelines
        </Typography>
        <Stack spacing={2}>
          <Typography variant="body2">
            <strong>Consistency:</strong> Use the defined spacing scale consistently throughout your designs.
          </Typography>
          <Typography variant="body2">
            <strong>Hierarchy:</strong> Use larger spacing to separate major sections and smaller spacing for related elements.
          </Typography>
          <Typography variant="body2">
            <strong>Responsive:</strong> Consider how spacing scales on different screen sizes.
          </Typography>
          <Typography variant="body2">
            <strong>Breathing Room:</strong> Don't be afraid of white space - it improves readability and user experience.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Spacing;